import { HttpClient, HttpHeaders, HttpParams } from "@angular/common/http";
import { defer } from "rxjs";
import { map } from "rxjs/operators";
import { Observable } from "rxjs/internal/Observable";

import { ConfigurationService } from "@core/configuration.service";
import * as SccryptoAbstraction from "@helper/abstraction/sccrypto";
import { UserSignToken } from "@helper/abstraction/user";
import { DraftDto } from "@helper/abstraction/draft";

export class Crypto {
	public get apiUrl(): string { return `${this.config.signingServiceNew}/SCCrypto`; }
	public get apiMobile(): string { return `${this.config.api.root}/cryptomobile`; }
	public get api(): string { return `${this.config.api.user}`; }
	public get configApi(): string { return `${this.config.api.root}`; }

	public certificates = {
		get$: (): Observable<SccryptoAbstraction.CertificatesCryptoDto> => {
			const url = `${this.apiUrl}/ra/certs`;
			return this.http.get<SccryptoAbstraction.CertificatesCryptoDto>(url);
		}
	};

	public signA = {
		post$: (params: SccryptoAbstraction.SignAParams): Observable<SccryptoAbstraction.SignedSCCryptoDto> => {
			const url = `${this.apiUrl}/ra/signa`;
			return this.http.post<SccryptoAbstraction.SignedSCCryptoDto>(url, params);
		}
	};

	public getSignRequest = {
		get$: (id: number, isVersion = false): Observable<SccryptoAbstraction.MobileEdsOperation> => {
			const url = `${this.handleApi(isVersion)}/signRequest/${id}`;
			return this.http.get<SccryptoAbstraction.MobileEdsOperation>(url);
		}
	};

	public signatureMobileTypeOperation = {
		post$: (mobileEdsMsg: SccryptoAbstraction.MobileEdsMsg, type: string, operation: string, isVersion = false): Observable<SccryptoAbstraction.MobileEdsOperation> => {
			const url = `${this.handleApi(isVersion)}/${type}/${operation}`;
			return this.http.post<SccryptoAbstraction.MobileEdsOperation>(url, mobileEdsMsg, { withCredentials: true });
		}
	};

	public authorizeCryptoMobile = {
		post$: (): Observable<SccryptoAbstraction.MobileEdsAuth> => {
			const url = `${this.apiMobile}/authorize`;
			return this.http.post<SccryptoAbstraction.MobileEdsAuth>(url, null, { withCredentials: true });
		}
	};

	public attachCryptoMobile = {
		post$: (mobileEds: SccryptoAbstraction.MobileEdsAuth): Observable<SccryptoAbstraction.MobileEdsAuth> => {
			const url = `${this.apiMobile}/attach`;
			return this.http.post<SccryptoAbstraction.MobileEdsAuth>(url, mobileEds, { withCredentials: true });
		}
	};

	public detachCryptoMobile = {
		post$: (): Observable<void> => {
			const url = `${this.apiMobile}/detach`;
			return this.http.post<void>(url, null, { withCredentials: true });
		}
	};

	public signD = {
		post$: (draftDto: DraftDto): Observable<SccryptoAbstraction.SignedSCCryptoDto> => {
			const url = `${this.apiUrl}/ra/signd`;
			let dataWithUNP;
			const data = {
				Auth: {
					CryptoType: 1,
					KeyID: draftDto.publicId,
					Password: draftDto.password
				},
				Data: draftDto.xmlByteDate,
				DataB64: draftDto.xmlBase64,
				OptAddAllCert: false,
				OptAddCert: true
			};
			if (data.DataB64) {
				delete data.Data;
			}
			if (draftDto.unp)
				dataWithUNP = {
					...data,
					Attributes: [{
						OID: "1.2.112.1.2.1.1.1.1.2",
						Text: draftDto.unp
					}]
				};
			return this.http.post<SccryptoAbstraction.SignedSCCryptoDto>(url, dataWithUNP || data);
		}
	};

	public bindCertificateToUser = {
		put$: (params: SccryptoAbstraction.AttachSignParams): Observable<SccryptoAbstraction.CertificatesCryptoDto> => {
			const url = `${this.api}/attachSign`;
			return this.http.put<SccryptoAbstraction.CertificatesCryptoDto>(url, params, { withCredentials: true });
		}
	};

	public unbindCertificateByUser = {
		put$: (): Observable<void> => {
			const url = `${this.api}/detachSign`;
			return this.http.put<void>(url, {}, { withCredentials: true });
		}
	};

	public getTokenForCertificateBinding = {
		get$: (base64?: boolean): Observable<UserSignToken> => {
			const url = `${this.api}/signToken`;
			let params = new HttpParams();
			if (typeof base64 === "boolean") {
				params = params.set("base64", base64!.toString());
			}
			return this.http.get<UserSignToken>(url, { withCredentials: true, params });
		}
	};

	public getActualVersionSC_Crypto = {
		get$: (): Observable<string> => {
			const url = `${this.configApi}/config/signConfig`;
			return this.http.get<{ cryptoProxyVersion: string }>(url, { withCredentials: true }).pipe(map(versionObj => versionObj.cryptoProxyVersion));
		}
	};

	public callbackCryptoMobile = {
		post$: (query: string): Observable<any> => {
			const url = `api/v1/cryptomobile/oauth/callback${query}`;
			return this.http.post<any>(url, { withCredentials: true });
		}
	};

	public signCryptoMobile = {
		post$: (id: number): Observable<any> => {
			return this.http.post<any>(`api/v1/cryptomobile/oauth/sign/${id}`, null);
		}
	};

	public cryptcreator = {
		// TODO
		getSignedEcmr$: (ecmrData: SccryptoAbstraction.DetachedCryptoDto, url: string): Observable<any> => { // TODO data type
			// const url = "http://192.168.246.172:8002/cryptcreator";
			return defer(()=>{
				return fetch(url, {
					  method: "POST",
					  body: JSON.stringify(ecmrData)
				  })
				  .then(response => response.json())
				  .then(json => {
					  return json;
				  })
			  });
		}
		// TODO
	};

	private handleApi(isVersion: boolean): string {
		return this.config.handleVersionPath(this.apiMobile, isVersion ? undefined : "");
	}

	constructor(
		private config: ConfigurationService,
		private http: HttpClient
	) { }
}
